/** @jsx jsx */
import React, { useState, useMemo, useCallback, useRef } from "react";
import { Modal } from "react-bootstrap";
import { jsx, css } from "@emotion/react";
import TmReactTable from "../../utilComponents/tmReactTable/tmReactTable";
import TmExportExcel from "../../utilComponents/tmExportExcel/tmExportExcel";
import FullScreen from "react-request-fullscreen";
import DispatchReview from "../../../containers/dispatcher/dispatchReview";
import StatusFilter from "./StatusFilter";
import { callStatus, priorities } from "../../../util/constants";
import {
  isManagement,
  setDashboardFilterByType,
  dashboardFilterByType,
} from "../../../util/customMethods";
import moment from "moment";

// ─── Emotion Styles ─────────────────────────────────────────────────────────

const PRIORITY_BORDERS = {
  1: "#e74c3c",
  2: "#9b59b6",
  3: "#e67e22",
  4: "#f1c40f",
  5: "#27ae60",
  6: "#3498db",
  7: "#8b6914",
  8: "#bdc3c7",
  9: "#2c3e50",
};

const STATUS_COLORS = {
  unassigned: "#dc3545",
  requested: "#9b59b6",
  onhold: "#fd7e14",
  assigned: "#007bff",
  accepted: "#0069d9",
  enroute: "#17a2b8",
  arrived: "#20c997",
  hooked: "#28a745",
  dropped: "#218838",
  cleared: "#6c757d",
  cancel: "#c0392b",
};

const rowStyle = (priority) => css`
  border-left: 4px solid ${PRIORITY_BORDERS[priority] || "#dee2e6"};
  cursor: pointer;
  transition:
    background 0.12s,
    box-shadow 0.12s;
  &:hover {
    background: #f0f6ff !important;
    box-shadow: inset 0 0 0 1px rgba(0, 123, 255, 0.15);
  }
`;

const statusBadge = (status) => css`
  display: inline-block;
  padding: 2px 8px;
  font-size: 0.68rem;
  font-weight: 600;
  border-radius: 3px;
  letter-spacing: 0.3px;
  text-transform: uppercase;
  color: #fff;
  background: ${STATUS_COLORS[(status || "").toLowerCase()] || "#6c757d"};
`;

const invoiceIdStyle = css`
  font-weight: 700;
  font-size: 0.88rem;
  color: #2c3e50;
  cursor: pointer;
  &:hover {
    color: #007bff;
    text-decoration: underline;
  }
`;

const locationBlock = css`
  font-size: 0.8rem;
  line-height: 1.55;
  & .lbl {
    font-weight: 600;
    color: #2a5c8a;
    font-size: 0.72rem;
    text-transform: uppercase;
    letter-spacing: 0.3px;
    margin-right: 4px;
  }
`;

const elapsedStyle = css`
  text-align: center;
  line-height: 1.4;
  & .elapsed-val {
    font-size: 0.92rem;
    font-weight: 700;
    color: #e67e22;
    display: block;
  }
  & .eta-val {
    font-size: 0.75rem;
    font-weight: 600;
    color: #495057;
    font-style: italic;
  }
`;

const metaLabel = css`
  font-size: 0.7rem;
  font-weight: 600;
  color: #6c757d;
  text-transform: uppercase;
  letter-spacing: 0.3px;
`;

const metaVal = css`
  font-size: 0.82rem;
  color: #2c3e50;
  font-weight: 500;
`;

const autoAssignLabel = css`
  font-size: 0.65rem;
  color: #007bff;
  font-weight: 500;
  animation: pulse 2s ease-in-out infinite;
  @keyframes pulse {
    0%,
    100% {
      opacity: 1;
    }
    50% {
      opacity: 0.5;
    }
  }
`;

const arsBadgeNew = css`
  display: inline-block;
  font-size: 0.6rem;
  font-weight: 700;
  padding: 1px 6px;
  border-radius: 3px;
  background: #28a745;
  color: #fff;
  margin-right: 4px;
`;

const arsBadgeVerified = css`
  display: inline-block;
  font-size: 0.6rem;
  font-weight: 700;
  padding: 1px 6px;
  border-radius: 3px;
  background: #007bff;
  color: #fff;
  margin-right: 4px;
`;

const priceBadge = css`
  display: inline-block;
  font-size: 0.6rem;
  font-weight: 700;
  padding: 1px 6px;
  border-radius: 3px;
  background: #ffc107;
  color: #333;
  animation: blink 1.5s ease-in-out infinite;
  @keyframes blink {
    0%,
    100% {
      opacity: 1;
    }
    50% {
      opacity: 0.3;
    }
  }
`;

const missingCoord = css`
  color: #dc3545;
`;

const toolBtn = css`
  font-size: 0.75rem;
  padding: 4px 10px;
  border-radius: 4px;
`;

// ─── Action Groups ──────────────────────────────────────────────────────────

const ACTION_GROUPS = {
  unassigned: ["Unassigned", "OnHold", "Requested"],
  inProgress: ["Assigned", "Accepted", "Enroute", "Arrived", "Hooked"],
  dropped: ["Dropped"],
  cleared: ["Cleared"],
};

// ─── Component ──────────────────────────────────────────────────────────────

const CallVisibility = ({
  data = [],
  routeInv,
  onCallActionBtnClick,
  canClear,
  canAssign,
  canCancel,
}) => {
  const isMgmt = isManagement();
  const fullScreenRef = useRef(null);
  const containerRef = useRef(null);

  const [byStatus, setByStatus] = useState(
    () => dashboardFilterByType("byStatus") || callStatus,
  );
  const [byPriority, setByPriority] = useState(
    () => dashboardFilterByType("byPriority") || priorities,
  );
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [cancelModal, setCancelModal] = useState({
    show: false,
    invoice: null,
    note: "Canceled",
  });
  const [clearModal, setClearModal] = useState({ show: false, invoice: null });
  const [reviewModal, setReviewModal] = useState({
    show: false,
    invoiceId: "",
  });

  // ─── Filtered data ──────────────────────────────────────────────────
  const filteredData = useMemo(() => {
    const selStatus = Object.entries(byStatus)
      .filter(([, v]) => v.selected)
      .map(([k]) => k.toLowerCase());
    const selPri = Object.entries(byPriority)
      .filter(([, v]) => v.selected)
      .map(([k]) => parseInt(k));
    return data.filter((item) => {
      const sm =
        selStatus.length === 0 ||
        selStatus.includes((item.callStatus || "").toLowerCase());
      const pm = selPri.length === 0 || selPri.includes(item.priority);
      return sm && pm;
    });
  }, [data, byStatus, byPriority]);

  // ─── Handlers ───────────────────────────────────────────────────────
  const handleStatusChange = useCallback((obj) => {
    setByStatus(obj);
    setDashboardFilterByType("byStatus", obj);
  }, []);

  const handlePriorityChange = useCallback((obj) => {
    setByPriority(obj);
    setDashboardFilterByType("byPriority", obj);
  }, []);

  const handleRowClick = useCallback(
    (row) => {
      const needsReview =
        row.isArsCall === 1 && !(row.ratePlanName || "").trim();
      if (needsReview) {
        setReviewModal({ show: true, invoiceId: row.invoiceId });
      } else {
        routeInv(row.invoiceId);
      }
    },
    [routeInv],
  );

  const handleAction = useCallback(
    (e, row, action) => {
      e.stopPropagation(); // prevent row click
      switch (action) {
        case "Cleared":
          if (row.isPoRequired === 1 && !(row.poNo || ""))
            setClearModal({ show: true, invoice: row });
          else
            onCallActionBtnClick({
              status: action,
              invoiceId: row.invoiceId,
              note: "",
            });
          break;
        case "Cancel":
          setCancelModal({ show: true, invoice: row, note: "Canceled" });
          break;
        case "Assign":
        case "Reassign":
          onCallActionBtnClick({
            status: action,
            invoiceId: row.invoiceId,
            note: "",
          });
          break;
        default:
          break;
      }
    },
    [onCallActionBtnClick],
  );

  const handleCancelSubmit = useCallback(() => {
    setCancelModal((prev) => {
      onCallActionBtnClick({
        status: "Cancel",
        invoiceId: prev.invoice.invoiceId,
        note: prev.note,
      });
      return { show: false, invoice: null, note: "Canceled" };
    });
  }, [onCallActionBtnClick]);

  const handleClearSubmit = useCallback(() => {
    setClearModal((prev) => {
      onCallActionBtnClick({
        status: "Cleared",
        invoiceId: prev.invoice.invoiceId,
        note: "",
      });
      return { show: false, invoice: null };
    });
  }, [onCallActionBtnClick]);

  const handleFullScreen = useCallback(() => {
    if (fullScreenRef.current)
      fullScreenRef.current.fullScreen(containerRef.current);
  }, []);

  // ─── Export ─────────────────────────────────────────────────────────
  const exportData = useMemo(
    () => [
      {
        columns: [
          { title: "Invoice #" },
          { title: "Call Status" },
          { title: "Requested By" },
          { title: "Rate Plan" },
          { title: "Reason" },
          { title: "Pickup Address" },
          { title: "Dropoff Address" },
          { title: "Driver" },
          { title: "Truck" },
          { title: "Tow" },
          { title: "Elapsed" },
          { title: "ETA" },
          { title: "ETD" },
        ],
        data: data.map((i) => [
          { value: i.invoiceId || "" },
          { value: i.callStatus || "" },
          { value: i.accountName || "" },
          { value: i.ratePlanName || "" },
          { value: i.reason || "" },
          { value: i.pickupAddress || "" },
          { value: i.dropoffAddress || "" },
          { value: i.userId || "" },
          { value: i.truckNo || "" },
          { value: i.towType || "" },
          { value: i.elapsedTime || "" },
          { value: i.etaTime || "" },
          { value: i.etdTime || "" },
        ]),
      },
    ],
    [data],
  );

  // ─── Table Columns ──────────────────────────────────────────────────
  const driverHeader = isMgmt ? "Driver | Truck | Company" : "Driver | Truck";

  const columns = useMemo(
    () => [
      {
        Header: "Invoice# | Status",
        accessor: "invoiceId",
        width: 140,
        Cell: (row) => {
          const r = row.original;
          const isArsNew = r.isArsCall === 1 && !(r.ratePlanName || "").trim();
          const isArsVerified =
            r.isArsCall === 1 && (r.ratePlanName || "").trim();
          return (
            <div
              css={rowStyle(r.priority)}
              className="py-1 px-2"
              onClick={() => handleRowClick(r)}
            >
              <div className="d-flex align-items-center mb-1">
                {isArsNew && <span css={arsBadgeNew}>ARS NEW</span>}
                {isArsVerified && <span css={arsBadgeVerified}>ARS ✓</span>}
                {r.isPaymentConfirmed && <span css={priceBadge}>$ PRICE</span>}
              </div>
              <div css={invoiceIdStyle}>{r.displayInvoiceId}</div>
              <span css={statusBadge(r.callStatus)}>{r.callStatus}</span>
              {!(r.autoAssignStatus || "") && !r.disableAutoAssign && (
                <div css={autoAssignLabel}>Auto Assign In-Progress</div>
              )}
            </div>
          );
        },
        filterMethod: (filter, row) =>
          (row._original.invoiceId || "").includes(filter.value) ||
          (row._original.callStatus || "")
            .toLowerCase()
            .startsWith(filter.value.toLowerCase()),
      },
      {
        Header: "Requested By",
        accessor: "accountName",
        width: 125,
        style: { whiteSpace: "normal" },
        Cell: (row) => (
          <div className="py-1">
            <div css={metaVal}>{row.original.accountName}</div>
            {row.original.towType && (
              <small className="text-muted">{row.original.towType}</small>
            )}
          </div>
        ),
      },
      {
        Header: "Reason | Rate Plan",
        id: "reason",
        width: 140,
        style: { whiteSpace: "normal" },
        Cell: (row) => (
          <div className="py-1">
            <div css={metaVal}>{row.original.reason}</div>
            {row.original.ratePlanName && (
              <small className="text-muted d-block">
                {row.original.ratePlanName}
              </small>
            )}
          </div>
        ),
        filterMethod: (filter, row) =>
          (row._original.reason || "")
            .toLowerCase()
            .includes(filter.value.toLowerCase()) ||
          (row._original.ratePlanName || "")
            .toLowerCase()
            .includes(filter.value.toLowerCase()),
      },
      {
        Header: "Locations",
        id: "location",
        Cell: (row) => (
          <div css={locationBlock} className="py-1">
            <div className="mb-1">
              <span className="lbl">Pick up</span>
              <span
                css={
                  !(row.original.pickupLatlang || "") ? missingCoord : undefined
                }
              >
                {row.original.pickupAddress || "—"}
              </span>
            </div>
            <div>
              <span className="lbl">Drop off</span>
              <span
                css={
                  !(row.original.dropLatlng || "") ? missingCoord : undefined
                }
              >
                {row.original.dropoffAddress || "—"}
              </span>
            </div>
          </div>
        ),
        filterMethod: (filter, row) =>
          (row._original.pickupAddress || "")
            .toLowerCase()
            .includes(filter.value.toLowerCase()) ||
          (row._original.dropoffAddress || "")
            .toLowerCase()
            .includes(filter.value.toLowerCase()),
      },
      {
        Header: driverHeader,
        accessor: "dri",
        width: 150,
        Cell: (row) => (
          <div className="py-1">
            <div>
              <span css={metaLabel}>Driver </span>
              <span css={metaVal}>{row.original.userId || "—"}</span>
            </div>
            <div>
              <span css={metaLabel}>Truck </span>
              <span css={metaVal}>{row.original.truckNo || "—"}</span>
            </div>
            {isMgmt && (
              <div>
                <span css={metaLabel}>Co. </span>
                <span css={metaVal}>{row.original.companyName || "—"}</span>
              </div>
            )}
          </div>
        ),
        filterMethod: (filter, row) =>
          (row._original.userId || "")
            .toLowerCase()
            .includes(filter.value.toLowerCase()) ||
          (row._original.truckNo || "")
            .toString()
            .toLowerCase()
            .includes(filter.value.toLowerCase()) ||
          (row._original.companyName || "")
            .toLowerCase()
            .includes(filter.value.toLowerCase()),
      },
      {
        Header: "Elapsed | ETA | ETD",
        accessor: "ela",
        filterable: false,
        width: 120,
        Cell: (row) => (
          <div css={elapsedStyle} className="py-1">
            <span className="elapsed-val">
              {row.original.elapsedTime || "—"}
            </span>
            <span className="eta-val">
              {row.original.etaTime} | {row.original.etdTime}
            </span>
          </div>
        ),
      },
      {
        Header: "Actions",
        accessor: "callStatus",
        filterable: false,
        width: 155,
        style: { textAlign: "center", overflow: "visible" },
        Cell: (row) => {
          const r = row.original;
          const status = r.callStatus;

          if (ACTION_GROUPS.dropped.includes(status)) return null;

          if (ACTION_GROUPS.cleared.includes(status)) {
            return canClear ? (
              <button
                className="btn btn-sm btn-success"
                css={toolBtn}
                onClick={(e) => handleAction(e, r, "Cleared")}
              >
                Clear
              </button>
            ) : null;
          }

          if (ACTION_GROUPS.inProgress.includes(status)) {
            return (
              <div className="btn-group btn-group-sm">
                {canAssign && (
                  <button
                    className="btn btn-outline-secondary"
                    css={toolBtn}
                    onClick={(e) => handleAction(e, r, "Reassign")}
                  >
                    Re/Unassign
                  </button>
                )}
                {canCancel && (
                  <button
                    className="btn btn-outline-warning"
                    css={toolBtn}
                    onClick={(e) => handleAction(e, r, "Cancel")}
                  >
                    Cancel
                  </button>
                )}
              </div>
            );
          }

          if (ACTION_GROUPS.unassigned.includes(status)) {
            return (
              <div className="btn-group btn-group-sm">
                {canAssign && (
                  <button
                    className="btn btn-success"
                    css={toolBtn}
                    onClick={(e) => handleAction(e, r, "Assign")}
                  >
                    Assign
                  </button>
                )}
                {canCancel && (
                  <button
                    className="btn btn-outline-warning"
                    css={toolBtn}
                    onClick={(e) => handleAction(e, r, "Cancel")}
                  >
                    Cancel
                  </button>
                )}
              </div>
            );
          }

          return null;
        },
      },
    ],
    [
      driverHeader,
      isMgmt,
      handleRowClick,
      handleAction,
      canClear,
      canAssign,
      canCancel,
    ],
  );

  // ─── Render ─────────────────────────────────────────────────────────
  return (
    <>
      {/* Filters */}
      <div className="mb-2">
        <StatusFilter
          id="callStatus"
          label="Status:"
          options={byStatus}
          onChange={handleStatusChange}
        />
      </div>
      <div className="d-flex justify-content-between align-items-center mb-2 flex-wrap">
        <StatusFilter
          id="pri"
          label="Priority:"
          options={byPriority}
          onChange={handlePriorityChange}
        />
        <div className="d-flex" style={{ gap: "6px" }}>
          <button
            className="btn btn-sm btn-outline-primary"
            css={toolBtn}
            onClick={handleFullScreen}
            title="Fullscreen"
          >
            <i className="fa fa-expand"></i>
          </button>
          <TmExportExcel
            name="Call list"
            filename={`callList_${moment().format("MMDDYYhhmm")}`}
            fileExtension="xlsx"
            element={
              <button
                className="btn btn-sm btn-outline-success"
                css={toolBtn}
                title="Export"
              >
                <i className="fa fa-file-excel-o"></i>
              </button>
            }
            dataSet={exportData}
          />
        </div>
      </div>

      {/* Fullscreen table */}
      <FullScreen ref={fullScreenRef} onFullScreenChange={setIsFullScreen}>
        <div ref={containerRef} className="bg-white">
          {isFullScreen && (
            <TmReactTable
              filterable={false}
              sortable={false}
              showPagination={false}
              resizable={false}
              defaultPageSize={filteredData.length}
              minRows={0}
              data={filteredData}
              className="-striped -highlight"
              columns={columns}
            />
          )}
        </div>
      </FullScreen>

      {/* Main table */}
      <div className="mt-2">
        <TmReactTable
          filterable
          sortable
          resizable
          defaultPageSize={25}
          data={filteredData}
          columns={columns}
          getTrProps={(state, rowInfo) => ({
            onClick: () => rowInfo && handleRowClick(rowInfo.original),
            onMouseEnter: (e) => {
              e.currentTarget.style.background = "#f0f6ff";
            },
            onMouseLeave: (e) => {
              e.currentTarget.style.background = "";
            },
          })}
        />
      </div>

      {/* Cancel Reason Modal */}
      <Modal
        show={cancelModal.show}
        onHide={() =>
          setCancelModal({ show: false, invoice: null, note: "Canceled" })
        }
      >
        <Modal.Header closeButton>
          <Modal.Title>Cancel Reason</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="form-group">
            <label className="font-weight-bold">
              Enter the reason to cancel
            </label>
            <input
              type="text"
              className="form-control"
              value={cancelModal.note}
              onChange={(e) =>
                setCancelModal((prev) => ({ ...prev, note: e.target.value }))
              }
              autoFocus
            />
          </div>
        </Modal.Body>
        <Modal.Footer>
          <button
            className="btn btn-sm btn-secondary"
            onClick={() =>
              setCancelModal({ show: false, invoice: null, note: "Canceled" })
            }
          >
            Close
          </button>
          <button
            className="btn btn-sm btn-danger"
            onClick={handleCancelSubmit}
          >
            Submit
          </button>
        </Modal.Footer>
      </Modal>

      {/* PO Required Modal */}
      <Modal
        show={clearModal.show}
        onHide={() => setClearModal({ show: false, invoice: null })}
      >
        <Modal.Header closeButton>
          <Modal.Title>PO Required</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            P.O. is required for this account. Press <strong>Cancel</strong> to
            go back and enter P.O. or <strong>Clear</strong> to proceed without
            P.O.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <button
            className="btn btn-sm btn-secondary"
            onClick={() => setClearModal({ show: false, invoice: null })}
          >
            Cancel
          </button>
          <button
            className="btn btn-sm btn-success"
            onClick={handleClearSubmit}
          >
            Clear
          </button>
        </Modal.Footer>
      </Modal>

      {/* ARS Review Modal */}
      <Modal
        show={reviewModal.show}
        onHide={() => setReviewModal({ show: false, invoiceId: "" })}
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>Dispatch Review</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <DispatchReview
            dispatchReInvoice={reviewModal.invoiceId}
            dispatchReview={reviewModal.show}
            onClose={() => setReviewModal({ show: false, invoiceId: "" })}
          />
        </Modal.Body>
      </Modal>
    </>
  );
};

export default React.memo(CallVisibility);
